<script setup lang="ts">
import SkeletonBlock from './SkeletonBlock.vue'

withDefaults(
  defineProps<{
    /** 是否展示筛选面板骨架 */
    showFilter?: boolean
    /** 筛选字段列数（桌面端 grid） */
    filterFields?: number
    /** 是否展示工具栏按钮区 */
    showToolbar?: boolean
    /** 表格列数 */
    columns?: number
    /** 表格行数 */
    rows?: number
    /** 是否展示面板标题 */
    showTitle?: boolean
  }>(),
  {
    showFilter: true,
    filterFields: 4,
    showToolbar: true,
    columns: 6,
    rows: 8,
    showTitle: true,
  },
)
</script>

<template>
  <div class="sk-list-page" aria-busy="true" aria-label="列表加载中">
    <div v-if="showFilter || showToolbar" class="sk-list-page__panel sk-surface manage-panel">
      <SkeletonBlock v-if="showTitle" variant="title" width="168px" class="sk-list-page__title" />
      <div
        v-if="showFilter"
        class="sk-list-page__filter-grid"
        :style="{ '--sk-filter-cols': filterFields }"
      >
        <div v-for="i in filterFields" :key="`f-${i}`" class="sk-list-page__filter-item">
          <SkeletonBlock variant="text" width="72px" inline />
          <SkeletonBlock variant="input" class="sk-list-page__filter-control" />
        </div>
      </div>
      <div v-if="showToolbar" class="sk-list-page__toolbar">
        <div class="sk-list-page__toolbar-group">
          <SkeletonBlock variant="button" width="88px" inline />
          <SkeletonBlock variant="button" width="88px" inline />
        </div>
        <div class="sk-list-page__toolbar-group">
          <SkeletonBlock variant="button" width="72px" inline />
          <SkeletonBlock variant="button" width="72px" inline />
        </div>
      </div>
    </div>

    <div class="sk-list-page__table sk-surface table-card">
      <div class="sk-list-page__thead">
        <SkeletonBlock
          v-for="c in columns"
          :key="`th-${c}`"
          variant="text"
          height="14px"
          class="sk-list-page__th-cell"
        />
      </div>
      <div v-for="r in rows" :key="`tr-${r}`" class="sk-list-page__tbody-row">
        <SkeletonBlock
          v-for="c in columns"
          :key="`td-${r}-${c}`"
          variant="text"
          :width="c === 1 ? '72%' : c === columns ? '56%' : '88%'"
          class="sk-list-page__td-cell"
        />
      </div>
      <div class="sk-list-page__pager">
        <SkeletonBlock variant="button" width="28px" inline />
        <SkeletonBlock variant="button" width="28px" inline />
        <SkeletonBlock variant="text" width="120px" inline />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sk-list-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.sk-list-page__panel {
  padding: var(--space-md) var(--space-lg);
}

.sk-list-page__title {
  margin-bottom: var(--space-md);
}

.sk-list-page__filter-grid {
  display: grid;
  grid-template-columns: repeat(var(--sk-filter-cols, 4), minmax(0, 1fr));
  gap: 12px 16px;
}

@media (max-width: 1200px) {
  .sk-list-page__filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.sk-list-page__filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sk-list-page__filter-control {
  flex: 1;
  min-width: 0;
}

.sk-list-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.sk-list-page__toolbar-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sk-list-page__table {
  padding: var(--space-md) var(--space-lg) var(--space-lg);
}

.sk-list-page__thead,
.sk-list-page__tbody-row {
  display: grid;
  grid-template-columns: repeat(var(--sk-cols, 6), minmax(72px, 1fr));
  gap: 12px;
  align-items: center;
}

.sk-list-page__thead {
  --sk-cols: v-bind(columns);
  padding: 12px 8px;
  margin-bottom: 4px;
  background: var(--surface-table-header);
  border-radius: var(--sk-radius-md);
}

.sk-list-page__tbody-row {
  --sk-cols: v-bind(columns);
  min-height: 52px;
  padding: 10px 8px;
  border-bottom: 1px solid var(--ds-border-subtle);
}

.sk-list-page__tbody-row:last-of-type {
  border-bottom: none;
}

.sk-list-page__th-cell,
.sk-list-page__td-cell {
  min-width: 0;
}

.sk-list-page__pager {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: var(--space-md);
  padding-top: var(--space-sm);
}

@media (max-width: 991px) {
  .sk-list-page__panel,
  .sk-list-page__table {
    padding: var(--space-md);
  }

  .sk-list-page__thead,
  .sk-list-page__tbody-row {
    min-width: 640px;
  }

  .sk-list-page__table {
    overflow-x: auto;
  }
}
</style>
